import { type ExtensionInfo, getExtensionInfo } from "./getExtensionInfo.ts";
import { splitPath } from "./splitPath.ts";

export interface FileNameInfo {
  /** Extracted filename in case of a path */
  filename?: string;
  extension: string | "unknown";
  isArchive: boolean;
  /** No lookbehind support */
  isWeak: boolean;
  /** Add "Archive" to the extensionInfo.typeLabel */
  typeLabel: string;
  extensionInfo: ExtensionInfo | null;
}

const ARCHIVE_EXTENSIONS = ["tar", "gz", "bgz", "gzip", "zip", "bz2", "lzo", "xz", "tgz", "rar", "7z", "sit"];
const ARCHIVE_EXTENSIONS_REGEX = ARCHIVE_EXTENSIONS.join("|");

/**
 * - Must start with a dot (this is captured -> Need to check/return the $1st captured group (= $2 + $3), not the complete match)
 * - A word which is not a compression extension -> So that in .tar.gz, .tar will not be captured, but only the word before, as tar is also compression extension
 *    + Followed by a dot -> The archive extension comes next
 *    + And a compression extension
 *    + And another dot or the end of the line -> Will prevent "sites" to match "sit" for instance
 * - OR the last word after the dot (the first one above) which is not a compression expression (like .fasta)
 *
 * ! my.filename.zip will be of type "filename". But it will very likely be an unknown file extension anyway.
 * ! filename.zip will, in contrary, be reported as unknown directly.
 */
const uncompressedExtensionRegExp = new RegExp(
  `\\.((?:[a-zA-Z0-9]+)[^\\s\\.]*(?:\\s+[^\\.]*?)?)(?<!\\b(${ARCHIVE_EXTENSIONS_REGEX})\\b)(?=(\\.\\b(${ARCHIVE_EXTENSIONS_REGEX})\\b(\\.|\\s|\\[|$))|$)`,
  "gi",
);

/**
 * Extract the extension if it is a compressed/archive file
 * Fallback for browsers without lookbehind support
 */
const weakUncompressedExtensionRegExp = new RegExp(
  `\\.(\\w+)(?=\\.\\b(${ARCHIVE_EXTENSIONS_REGEX})\\b(\\.|\\s|\\[|$))`,
  "gi",
);

/** Simply testing that the last extension is an archive/compression extension */
const archiveRegExp = new RegExp(`\\.(${ARCHIVE_EXTENSIONS_REGEX})(?:\\s|\\[|$)`, "i");

// Simply extract the last extension
const extensionRegExp = /\.(\w+)$/i;

/**
 * Will return information about a filename.
 * Compatible with archive/compression extensions.
 * @see FileDefinition.ARCHIVE
 */
export const getFilenameInfo = (path?: string | null): FileNameInfo | null => {
  // Extract filename from path, just in case.
  // Also return null if "" (falsy value)
  const filename = path ? splitPath(path).pop() : undefined;

  if (!filename) return null;

  let extResult: RegExpMatchArray | null | undefined;
  let isWeak = false; // No lookbehind support

  // In "file.name.tar.sites" it will match with ".(name)" because of the ".tar" before it.
  // It will also match with ".(sites) which is the correct answer.
  // The last one in the matchAll is always the correct match as the correct extension is always at the end.
  try {
    // This will crash when lookbehind is not supported. But will catch all extensions correctly.
    extResult = [...filename.matchAll(uncompressedExtensionRegExp)].pop();
  } catch {
    // This finds only extension before the compressed extension
    extResult = [...filename.matchAll(weakUncompressedExtensionRegExp)].pop();
  }

  if (extResult == null) {
    // In case regex lookbehind is not supported or the weaker regex was not able to extract extension because the file is not a compressed one,
    // we just extract the last extensions. Currently, FF < 78ESR and Safari < 16.4 are not up-to-date. https://bugs.webkit.org/show_bug.cgi?id=174931
    extResult = filename.match(extensionRegExp);
    isWeak = true; // Will use the full filename for type detection
  }

  // In all cases, the first capturing group is the extension without the dot
  const extension = extResult?.[1] ?? "unknown";
  const isArchive = !!filename.match(archiveRegExp);
  const extensionInfo = getExtensionInfo(extension);

  return {
    filename,
    extension,
    isArchive,
    isWeak,
    extensionInfo,
    typeLabel: `${extensionInfo?.typeLabel ?? extension.toUpperCase() + " File"}`,
  };
};
